import apiClient from "../../client/apiClient"
import { CONDITIONS } from "../../constant"
import { appendQueryParams } from "../../utils"

export type Book = {
  _id: string
  title: string
  author: string
  publisher: string
  quantity: number
  classificationCode: string
  subClassificationCode?: string
  condition: (typeof CONDITIONS)[keyof typeof CONDITIONS]
  price: number
  available_quantity: number
  bookIdentifier: string
  createdAt: string
  updatedAt: string
  isbn?: string
  cabinetNo?: string
  shelfNo?: string
}

export type CreateBookPayload = Omit<
  Book,
  "_id" | "createdAt" | "updatedAt" | "available_quantity" | "bookIdentifier"
>

export type UpdateBookData = Partial<CreateBookPayload> & {
  is_active?: boolean
}

export type PaginationData = {
  currentPage: number
  hasNextPage: boolean
  hasPrevPage: boolean
  limit: number
  total: number
  totalPages: number
}

export type BooksResponse = {
  success: boolean
  data: Book[]
  pagination: {
    total: number
    totalPages: number
    currentPage: number
    limit: number
  }
}

export type BookResponse = {
  success: boolean
  data: Book
}

export type BookItem = {
  _id: string
  book: string
  copyIdentifier: string
  barcode?: string
  status: "available" | "issued" | "lost" | "damaged"
  condition?: string
  cabinetNo?: string
  shelfNo?: string
  createdAt?: string
  updatedAt?: string
}

export type BookItemSearchResponse = {
  success: boolean
  data: {
    item: BookItem
    book: Book
    issue?: any
  }
}

export type BookItemsResponse = {
  success: boolean
  data: BookItem[]
  pagination?: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

export type MessageResponse = {
  success: boolean
  message: string
  data?: Book
}

const BOOKS_ENDPOINT = {
  BOOKS: "/library/books",
}

export const getAllBooks = async ({
  page = 1,
  limit = 10,
  sortBy,
  sortOrder,
  searchText,
  category,
  author,
  branch,
  available,
  bookNo,
}: {
  page?: number
  limit?: number
  sortBy?: string
  sortOrder?: string
  searchText?: string
  category?: string
  author?: string
  branch?: string
  available?: string
  bookNo?: string
} = {}): Promise<BooksResponse> => {
  const response = await apiClient({
    url: appendQueryParams(BOOKS_ENDPOINT.BOOKS, {
      page,
      limit,
      sortBy,
      sortOrder,
      searchText,
      category,
      author,
      branch,
      available,
      isbn: bookNo,
    }),
    method: "GET",
  })
  return response.data
}

// Create new book
export const createBook = async (
  data: CreateBookPayload,
): Promise<BookResponse> => {
  const response = await apiClient({
    url: BOOKS_ENDPOINT.BOOKS,
    method: "POST",
    data,
  })
  return response.data
}

// Update book
export const updateBook = async (
  id: string,
  data: UpdateBookData,
): Promise<BookResponse> => {
  const response = await apiClient({
    url: `${BOOKS_ENDPOINT.BOOKS}/${id}`,
    method: "PUT",
    data,
  })
  return response.data
}

// Delete book (soft delete)
export const deleteBook = async (id: string): Promise<MessageResponse> => {
  const response = await apiClient({
    url: `${BOOKS_ENDPOINT.BOOKS}/${id}`,
    method: "DELETE",
  })
  return response.data
}

// Get book by ID
export const getBookById = async (id: string): Promise<BookResponse> => {
  const response = await apiClient({
    url: `${BOOKS_ENDPOINT.BOOKS}/${id}`,
    method: "GET",
  })
  return response.data
}

// Search for a book item by its barcode (global — no book ID required)
export const getBookItemByBarcode = async (
  barcode: string,
): Promise<BookItemSearchResponse> => {
  const response = await apiClient({
    url: `/library/books/items/barcode/${encodeURIComponent(barcode)}`,
    method: "GET",
  })
  return response.data
}

// Get physical copies (items) for a book
export const getBookItems = async (
  id: string,
  {
    status,
    page,
    limit,
    searchText,
  }: {
    status?: string
    page?: number
    limit?: number
    searchText?: string
  },
): Promise<BookItemsResponse> => {
  const response = await apiClient({
    url: appendQueryParams(`${BOOKS_ENDPOINT.BOOKS}/${id}/items`, {
      status,
      page,
      limit,
      searchText,
    }),
    method: "GET",
  })
  return response.data
}
